<template>
	<div class="flex w-full relative items-center">
		<Arrow
			v-if="showArrows"
			arrow-direction="left"
			:number-of-slides-to-show="blok.number_of_slides_to_show"
			:scroll-position="scrollPosition"
			:number-of-slides="slides.length"
			is-full-height
			@click="slideLeft"
		/>

		<div
			ref="carouselRef"
			class="flex cursor-pointer overflow-x-auto items-start w-full carousel pb-8"
			@mousedown="handleDragStart"
			@mouseup="handleDragEnd"
			@mouseleave="handleDragEnd"
			@mousemove="handleDrag"
		>
			<div
				v-for="slide in slides"
				:key="slide.Id"
				class="flex flex-col gap-4 text-center"
				:class="`${wrapperSize} ${typeOfSlide === ItemType.CATEGORY ? 'w-full max-w-[200px] lg:max-w-[28%]' : 'last:mr-0 first:ml-0 mx-2 flex-none w-auto sm:w-[40%] lg:w-[25%] lg:max-w-[364px]'}`"
			>
				<div
					v-if="typeOfSlide === ItemType.CATEGORY"
					class="block h-auto max-w-full"
				>
					<CategoryCard
						:category="slide as CategoryWithFiles"
						:lazy-loading="blok.lazy_load_images"
						:link="
							clickIsValid
								? localePath(
										`/${createCategoryLink({
											category: slide as CategoryWithFiles
										})}`
									)
								: ''
						"
					/>
				</div>
				<ProductCard
					v-if="typeOfSlide === ItemType.PRODUCT"
					:product="slide as ProductItem"
					picture-size="h-auto max-w-full w-max"
					:click-is-valid="clickIsValid"
					:lazy-loading="blok.lazy_load_images"
					:use-product-parent-url="blok.use_product_parent_url"
					:variants="
						(slide as ProductItem & { variants?: FilteredProductItem[] })
							.variants
					"
					:all-parametrics="allParametrics"
					:show-variants="showVariants"
					@product-click="$emit('product-click', slide)"
				/>
			</div>
		</div>

		<Arrow
			v-if="showArrows"
			arrow-direction="right"
			:number-of-slides-to-show="blok.number_of_slides_to_show"
			:scroll-position="scrollPosition"
			:number-of-slides="slides.length"
			is-full-height
			@click="slideRight"
		/>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useResizeObserver } from "@vueuse/core";
import type { CategoryWithFiles } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type {
	ProductItem,
	ParametricListValue
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type { BlokCarousel } from "~/types/storyblok/storyblok";
import { ItemType } from "~/utils/constants";

interface Props {
	blok: BlokCarousel;
	slides: CategoryWithFiles[] | ProductItem[];
	typeOfSlide: string;
	/** Parametric value catalog — lets product slides render colour swatches. */
	allParametrics?: ParametricListValue[];
	/** Show variant (colour) swatches on product cards when slides carry them. */
	showVariants?: boolean;
}

const { blok, slides, typeOfSlide, allParametrics, showVariants } =
	defineProps<Props>();
defineEmits(["product-click"]);

const localePath = useLocalePath();
const { createCategoryLink } = useCategory();
const carouselRef = ref<HTMLElement>();
const isMouseDown = ref(false);
const clickIsValid = ref(true);
const mouseCoords = ref({
	startX: 0,
	scrollLeft: 0
});

const pictureSizes = {
	six: 199,
	four: 280
};

const showArrows = computed(() => {
	return +blok.number_of_slides_to_show < slides?.length && blok.show_arrows;
});

const scrollPosition = ref(0);
let cancelClick: ReturnType<typeof setTimeout>;
const handleDragStart = (e: MouseEvent) => {
	if (!carouselRef.value) return;
	isMouseDown.value = true;
	cancelClick = setTimeout(() => {
		clickIsValid.value = false;
	}, 200);
	carouselRef.value.classList.remove("scroll-smooth");

	const slider = carouselRef.value;
	const startX = e.pageX - slider.offsetLeft;
	const scrollLeft = slider.scrollLeft;
	mouseCoords.value = { startX, scrollLeft };

	carouselRef.value.style.cursor = "grabbing";

	e.preventDefault();
};
const handleDragEnd = () => {
	clearTimeout(cancelClick);
	clickIsValid.value = true;
	isMouseDown.value = false;
	if (!carouselRef.value) return;
	carouselRef.value.style.cursor = "pointer";
};
const handleDrag = (e: MouseEvent) => {
	if (!isMouseDown.value || !carouselRef.value) return;
	e.preventDefault();

	const x = e.pageX - carouselRef.value.offsetLeft;
	const walkX = (x - mouseCoords.value.startX) * 1.5;
	const scrollLeft = mouseCoords.value.scrollLeft - walkX;
	carouselRef.value.scrollLeft = scrollLeft;
	scrollPosition.value = scrollLeft;
};

const slideRight = () => {
	if (!carouselRef.value) {
		return;
	}
	const stepSize =
		blok.number_of_slides_to_show === "4"
			? pictureSizes.four * 1.065
			: pictureSizes.six * 1.065;
	carouselRef.value.classList.add("scroll-smooth");
	const scrollLeft = carouselRef.value.scrollLeft + stepSize;
	carouselRef.value.scrollLeft = scrollLeft;
	scrollPosition.value = scrollLeft;
};

const slideLeft = () => {
	if (!carouselRef.value) {
		return;
	}
	if (!carouselRef.value.scrollLeft) {
		return;
	}
	const stepSize =
		blok.number_of_slides_to_show === "4"
			? pictureSizes.four * 1.065
			: pictureSizes.six * 1.065;
	carouselRef.value.classList.add("scroll-smooth");
	const scrollLeft = carouselRef.value.scrollLeft - stepSize;
	carouselRef.value.scrollLeft = scrollLeft;
	scrollPosition.value = scrollLeft;
};
const wrapperSize = ref<string>("");
const changeWrapperSize = () => {
	if (blok.number_of_slides_to_show === "6") {
		if (
			carouselRef?.value &&
			carouselRef?.value?.offsetWidth >
				+blok.number_of_slides_to_show * pictureSizes.six
		) {
			return (wrapperSize.value = "w-2/12");
		}
		return (wrapperSize.value = "");
	} else if (blok.number_of_slides_to_show === "4") {
		return `w-[${pictureSizes.four}px] h-[${pictureSizes.four}px]`;
	}
};

onMounted(async () => {
	changeWrapperSize();
});
useResizeObserver(carouselRef, () => {
	changeWrapperSize();
});
</script>
